import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { RefreshCw, Star } from "lucide-react";
import { useStoreStatus } from "@/context/StoreStatusContext";
import { useGoogleReviews } from "@/context/GoogleReviewsContext";

/**
 * Admin > Store panel
 *   · Manual open/close toggle
 *   · Auto-close every Monday toggle
 *   · Editable closed-day banner message
 *   · Launch cookie promo (active toggle + 100-cookie counter)
 *   · Waitlist reopen blast
 */
export default function StorePanel() {
  const { refresh } = useStoreStatus();
  const { data: reviewsData, refreshing: reviewsRefreshing, refresh: refreshReviews } = useGoogleReviews();
  const [invalidating, setInvalidating] = useState(false);

  const refreshGoogleReviews = async () => {
    setInvalidating(true);
    try {
      await api.post("/admin/reviews/google/invalidate");
      await refreshReviews({ force: true });
      toast.success("Google reviews refreshed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh Google reviews.");
    } finally {
      setInvalidating(false);
    }
  };

  // Store status form
  const [status, setStatus] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);

  // Launch promo form
  const [promo, setPromo] = useState(null);
  const [savingPromo, setSavingPromo] = useState(false);

  // Waitlist
  const [waitlist, setWaitlist] = useState(null);
  const [blasting, setBlasting] = useState(false);
  const [resendToAll, setResendToAll] = useState(false);

  const loadWaitlist = () =>
    api.get("/admin/waitlist").then(({ data }) => setWaitlist(data)).catch(() => {});

  useEffect(() => {
    api.get("/config/store-status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/config/launch-promo").then(({ data }) => setPromo(data)).catch(() => {});
    loadWaitlist();
  }, []);

  const saveStatus = async () => {
    setSavingStatus(true);
    try {
      const { data } = await api.put("/admin/config/store-status", {
        manual_closed: !!status.manual_closed,
        auto_close_monday: !!status.auto_close_monday,
        closed_message: status.closed_message || "",
      });
      setStatus(data);
      toast.success(data.is_open ? "Store is open." : "Store is now closed.");
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save store status.");
    } finally {
      setSavingStatus(false);
    }
  };

  const savePromo = async () => {
    setSavingPromo(true);
    try {
      const { data } = await api.put("/admin/config/launch-promo", {
        is_active: !!promo.is_active,
        limit: Math.max(0, parseInt(promo.limit, 10) || 0),
      });
      setPromo(data);
      toast.success("Launch promo saved.");
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save promo.");
    } finally {
      setSavingPromo(false);
    }
  };

  const sendBlast = async () => {
    const target = resendToAll ? waitlist?.count : waitlist?.pending;
    if (!target) {
      toast.info("No one to notify yet.");
      return;
    }
    const confirm = window.confirm(
      `Send the reopen announcement to ${target} ${target === 1 ? "subscriber" : "subscribers"}?`
    );
    if (!confirm) return;
    setBlasting(true);
    try {
      const { data } = await api.post("/admin/waitlist/blast", {
        include_previously_notified: resendToAll,
      });
      toast.success(`Sent ${data.sent} · Failed ${data.failed}`);
      await loadWaitlist();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send announcement.");
    } finally {
      setBlasting(false);
    }
  };

  if (!status || !promo) {
    return <div className="mt-10 label-xs text-espresso-light">Loading…</div>;
  }

  const pct = promo.limit ? Math.round((promo.used / promo.limit) * 100) : 0;

  return (
    <div className="mt-8 md:mt-10 grid gap-6 md:gap-8 md:grid-cols-2" data-testid="store-panel">
      {/* Store status card */}
      <div className="border border-line p-6 md:p-8 bg-ivory space-y-6" data-testid="store-status-card">
        <div>
          <div className="label-xs text-espresso-light">Store status</div>
          <h2 className="font-serif text-3xl text-espresso mt-2">
            {status.is_open ? "Open for orders" : "Closed today"}
          </h2>
          {!status.is_open && (
            <p className="mt-2 text-sm text-espresso-light italic">
              Reason: {status.reason === "manual" ? "manually closed by you" : "outside shift hours (Tue–Sun · 12 PM – 3 AM IST)"}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between border-b border-line pb-4">
          <div>
            <Label className="text-espresso">Manual close</Label>
            <p className="text-xs text-espresso-light mt-1">Force the store closed regardless of the day.</p>
          </div>
          <Switch
            checked={!!status.manual_closed}
            onCheckedChange={(v) => setStatus((s) => ({ ...s, manual_closed: v }))}
            data-testid="store-manual-close-toggle"
          />
        </div>

        <div className="flex items-center justify-between border-b border-line pb-4">
          <div>
            <Label className="text-espresso">Follow shift schedule</Label>
            <p className="text-xs text-espresso-light mt-1">
              Auto-close outside boutique hours (Tue–Sun · 12:00 PM – 3:00 AM IST). Monday off.
            </p>
          </div>
          <Switch
            checked={!!status.auto_close_monday}
            onCheckedChange={(v) => setStatus((s) => ({ ...s, auto_close_monday: v }))}
            data-testid="store-monday-toggle"
          />
        </div>

        <div>
          <Label className="text-espresso">Closed message</Label>
          <Textarea
            rows={2}
            value={status.closed_message || ""}
            onChange={(e) => setStatus((s) => ({ ...s, closed_message: e.target.value }))}
            data-testid="store-closed-message"
            className="mt-2"
            placeholder="We are closed today for baking! See you on Tuesday."
          />
        </div>

        <Button
          onClick={saveStatus}
          disabled={savingStatus}
          data-testid="store-status-save"
          className="w-full bg-espresso hover:bg-espresso-hover text-ivory"
        >
          {savingStatus ? "Saving…" : "Save store status"}
        </Button>
      </div>

      {/* Launch promo card */}
      <div className="border border-line p-6 md:p-8 bg-ivory space-y-6" data-testid="launch-promo-card">
        <div>
          <div className="label-xs text-espresso-light">Launch promotion</div>
          <h2 className="font-serif text-3xl text-espresso mt-2">Free cookie for first {promo.limit}</h2>
          <p className="mt-2 text-sm text-espresso-light">
            Auto-adds a ₹0 cookie to eligible orders. Your admin email is excluded from the count.
          </p>
        </div>

        <div>
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-espresso-light">Claimed</span>
            <span className="font-serif text-espresso">
              <span data-testid="launch-promo-used">{promo.used}</span> / {promo.limit}
              <span className="text-espresso-light ml-3">({promo.remaining} left)</span>
            </span>
          </div>
          <div className="mt-2 h-2 w-full bg-line/60 rounded">
            <div className="h-2 bg-gold rounded" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="flex items-center justify-between border-b border-line pb-4">
          <div>
            <Label className="text-espresso">Promotion active</Label>
            <p className="text-xs text-espresso-light mt-1">Turn off to stop attaching free cookies to new orders.</p>
          </div>
          <Switch
            checked={!!promo.is_active}
            onCheckedChange={(v) => setPromo((p) => ({ ...p, is_active: v }))}
            data-testid="launch-promo-active-toggle"
          />
        </div>

        <div>
          <Label className="text-espresso">Cookie limit</Label>
          <Input
            type="number"
            min="0"
            value={promo.limit}
            onChange={(e) => setPromo((p) => ({ ...p, limit: e.target.value }))}
            data-testid="launch-promo-limit"
            className="mt-2 max-w-[160px]"
          />
        </div>

        <Button
          onClick={savePromo}
          disabled={savingPromo}
          data-testid="launch-promo-save"
          className="w-full bg-espresso hover:bg-espresso-hover text-ivory"
        >
          {savingPromo ? "Saving…" : "Save promotion"}
        </Button>
      </div>

      {/* Google reviews cache — full width */}
      <div className="border border-line p-6 md:p-8 bg-ivory space-y-6 md:col-span-2" data-testid="google-reviews-card">
        <div>
          <div className="label-xs text-espresso-light">Google Business</div>
          <h2 className="font-serif text-3xl text-espresso mt-2">Live rating on the site</h2>
          <p className="mt-2 text-sm text-espresso-light">
            The rating badge in the header, footer, and Reviews page reads from a 6-hour cache
            so we don't blow through Places API quota. Tap refresh right after your first Google
            review lands so it appears on the site immediately.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="border border-line p-4 text-center" data-testid="gr-rating">
            <div className="label-xs text-espresso-light">Rating</div>
            <div className="font-serif text-3xl text-espresso mt-1 inline-flex items-center gap-2 justify-center">
              {reviewsData?.rating ? (
                <>
                  <Star size={18} strokeWidth={1.5} className="fill-gold text-gold" />
                  {Number(reviewsData.rating).toFixed(1)}
                </>
              ) : "—"}
            </div>
          </div>
          <div className="border border-line p-4 text-center" data-testid="gr-total">
            <div className="label-xs text-espresso-light">Reviews</div>
            <div className="font-serif text-3xl text-espresso mt-1">{reviewsData?.total ?? "—"}</div>
          </div>
          <div className="border border-line p-4 text-center" data-testid="gr-cached-at">
            <div className="label-xs text-espresso-light">Last refreshed</div>
            <div className="font-serif text-sm text-espresso mt-2 leading-snug">
              {reviewsData?.cached_at
                ? new Date(reviewsData.cached_at).toLocaleString("en-IN", {
                    dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata",
                  })
                : "Never"}
              {reviewsData?.stale && (
                <div className="label-xs text-gold-hover mt-1">Marked stale</div>
              )}
            </div>
          </div>
        </div>

        <Button
          onClick={refreshGoogleReviews}
          disabled={invalidating || reviewsRefreshing}
          data-testid="google-reviews-refresh-btn"
          className="w-full bg-espresso hover:bg-espresso-hover text-ivory inline-flex items-center justify-center gap-2"
        >
          <RefreshCw size={14} strokeWidth={1.75} className={reviewsRefreshing ? "animate-spin" : ""} />
          {invalidating || reviewsRefreshing ? "Syncing…" : "Sync Google Reviews"}
        </Button>
      </div>

      {/* Waitlist reopen blast — full width */}
      <div className="border border-line p-6 md:p-8 bg-ivory space-y-6 md:col-span-2" data-testid="waitlist-card">
        <div>
          <div className="label-xs text-espresso-light">Waitlist</div>
          <h2 className="font-serif text-3xl text-espresso mt-2">Reopen announcement</h2>
          <p className="mt-2 text-sm text-espresso-light">
            Emails captured on the Coming Soon page while <code>STORE_LIVE_MODE</code> was off.
            Send the "we're open" note the day you flip live.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="border border-line p-4 text-center" data-testid="waitlist-count">
            <div className="label-xs text-espresso-light">Total</div>
            <div className="font-serif text-3xl text-espresso mt-1">{waitlist?.count ?? "—"}</div>
          </div>
          <div className="border border-line p-4 text-center" data-testid="waitlist-pending">
            <div className="label-xs text-espresso-light">Pending</div>
            <div className="font-serif text-3xl text-espresso mt-1">{waitlist?.pending ?? "—"}</div>
          </div>
          <div className="border border-line p-4 text-center" data-testid="waitlist-notified">
            <div className="label-xs text-espresso-light">Already notified</div>
            <div className="font-serif text-3xl text-espresso mt-1">{waitlist?.notified ?? "—"}</div>
          </div>
        </div>

        <div className="flex items-center justify-between border-y border-line py-4">
          <div>
            <Label className="text-espresso">Resend to everyone</Label>
            <p className="text-xs text-espresso-light mt-1">
              Also include people who already received a previous reopen note.
            </p>
          </div>
          <Switch
            checked={resendToAll}
            onCheckedChange={setResendToAll}
            data-testid="waitlist-resend-all-toggle"
          />
        </div>

        <Button
          onClick={sendBlast}
          disabled={blasting || !waitlist || (resendToAll ? !waitlist.count : !waitlist.pending)}
          data-testid="waitlist-blast-btn"
          className="w-full bg-espresso hover:bg-espresso-hover text-ivory"
        >
          {blasting
            ? "Sending…"
            : `Send reopen announcement (${resendToAll ? (waitlist?.count ?? 0) : (waitlist?.pending ?? 0)})`}
        </Button>
      </div>
    </div>
  );
}

